// PATH: src/hooks/useTilmiAuth.ts
import { useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import type { TilmiAppUser } from "../types/auth";

const extractFullName = (user?: TilmiAppUser | null): string | null => {
    if (!user) return null;

    const personal = user.user?.personal_info;
    if (personal) {
        const firstname = personal.firstname?.trim() ?? "";
        const lastname = personal.lastname?.trim() ?? "";
        const combined = `${firstname} ${lastname}`.trim();
        if (combined) return combined;
    }

    const businessName = user.user?.business_info?.name?.trim();
    if (businessName) return businessName;

    const name = typeof user.name === "string" ? user.name.trim() : "";
    if (name) return name;

    const email = typeof user.email === "string" ? user.email.trim() : "";
    return email || null;
};

const extractTimeRemaining = (user?: TilmiAppUser | null): number => {
    if (!user?.metadata) return -1;
    const value = (user.metadata as Record<string, unknown>).time_remaining;
    if (typeof value === "number" && !Number.isNaN(value)) return value;
    if (typeof value === "string") {
        const parsed = Number(value);
        if (!Number.isNaN(parsed)) return parsed;
    }
    return -1;
};

export const useTilmiAuth = () => {
    const auth = useAuth<TilmiAppUser>();

    return useMemo(() => {
        const roles = auth.user?.roles ?? [];
        const userId = auth.user?.user_id ?? auth.user?.user?.id ?? null;
        const codeTypeUser = auth.user?.userTypeCode ?? null;
        const fullName = extractFullName(auth.user);
        const timeRemaining = extractTimeRemaining(auth.user);

        const isRoleExist = (role: string) => roles.includes(role);

        const getRoles = () => roles.slice();

        const setLang = async (lang: string) => {
            await auth.updateMe({ lang });
            if (typeof window !== "undefined") {
                try {
                    window.localStorage?.setItem("lang", lang);
                } catch {
                    // ignore storage errors (private mode, etc.)
                }
            }
            await auth.refreshMe();
        };

        return {
            ...auth,
            getUserAppId: () => (userId ?? 0),
            getCodeTypeUser: () => codeTypeUser,
            getFullName: () => fullName,
            getRoles,
            isRoleExist,
            getTimeRemaining: () => timeRemaining,
            setLang,
        } as const;
    }, [auth]);
};
